// İSG uzmanı portalı — "Bugün" satırının salt-okuma detay modalı (S32d Part Two, `docs/reviews/
// S32d-verdict-and-detail.md` §6/§7). Precedent: `VisitDetailModal.tsx` (aynı şekil — paylaşılan
// `Modal`, satırdan render edilen bir kimlik+durum bloğu, "Kapat" dışında hiçbir aksiyon) — TEK
// fark izin listesinin CANLI olması ve bu yüzden `GET /security/entries/{uuid}` (§29.5) ile AYRI
// bir istek gerektirmesi. Kimlik/karar/sebep bloğu ise HİÇBİR ek istek GEREKTİRMEZ — sekiz alanın
// altısı gün listesi satırıyla (`SecurityEntryRowDto`) BİREBİR aynıdır, `row` PROP'undan render
// edilir (tasarım spec'i §6.2 "ship-now set").
//
// **Bu bir KAYITTIR, bir KARAR EKRANI DEĞİL (owner talimatı, tasarım spec'i §5).** Onayla/Reddet
// YOK — dondurulmuş bir olayı "eyleme açık" gibi sunmak bir güvenlik sorunudur; TEK footer
// kontrolü `Kapat`. QR/"Dijital Erişim Kartı" çerçevesi de YOK — giriş jetonu 120 sn'lik
// ÇOK-KULLANIMLI bir sırdır, geçmiş bir kayıt modalında yeniden ÇİZMEK bir güvenlik gerilemesi
// olurdu. Maskeli TCKN, plaka, bölge, Geçiş No, yazdırma — hiçbiri bu yüzeyin VAAT ETMEMESİ
// gereken alanlar (KVKK + §29.5, tasarım spec'i §5 tablosu).
//
// **`liveAsOf` ile ETİKETLEME ZORUNLUDUR, `at` İLE DEĞİL (normatif, §29.5/§6.3).** `permits[]`
// OKUMA ANINDAKİ durumdur — donmuş `result`/`reason` ile GÖRÜNÜR biçimde çelişebilir (izin
// sonradan düzeltilmiş/iptal edilmiş olabilir; bu BEKLENEN bir davranıştır, hata DEĞİLDİR).
// Liste `security.detail.permits.asOf` ile `liveAsOf`'a bağlanır; `result==='Allowed'` VE hiçbir
// kalemde `entryPermitted===true` yoksa (boş liste DÂHİL) nötr `security.verdict.liveMismatch`
// notu çıkar. **Ters yönde çıkarım hiçbir zaman KURULMAZ** — bir ret satırının yanında geçerli
// görünen bir izin o reddin YANLIŞ olduğu anlamına gelmez; rette `PermitId` zaten `null`'dır
// (`docs/BACKLOG.md:444`), yani "hangi izin engelledi" sorusu bu yüzeyde YAPISAL olarak
// CEVAPLANAMAZ ve hiçbir satır bunu ima etmez.
//
// **Önbelleğe ALINMAZ (§29.5: "modal her açılışta yeniden çekilir ... kapanışta atılır").**
// `staleTime`/`gcTime` `0` — gün listesinin geçersiz kılınması bu sorguyu ETKİLEMEZ, satır hiç
// değişmez (log yalnız eklenir, §29.3), ikisi bağımsız okumalardır (§29.5 madde 3).
//
// **S32f (`docs/reviews/S32e-evidence-and-layout.md` PART FOUR, §14-§17/§21.2/§16) — H1/H2
// bu yüzeyde de kapanır.** `entryPermitted` bir HİBEDİR, YENİ altıncı alan `validityState`
// (`GateScanPermitDto`, `lib/permitWindow.ts`) AYRI bir PENCEREDİR — satırlar artık
// `rankPermits` (§17, `VerdictCard.tsx` ile AYNI fonksiyon, tek doğruluk kaynağı) ile sıralanır
// ve rozet üç durumlu (`permitChip`, §16): aktif+hibe yeşil, aktif hibesiz kırmızı, `future`/
// `expired` NÖTR (`Calendar`/`CalendarOff`). Alt satıra pencere dışı satırlarda [taste, §16]
// hibe olgusu geçmiş-zaman ekiyle EKLENİR ("giriş izni verilmiş/verilmemiş") — rozet artık
// PENCEREYİ söylediğinden hibe olgusu BAŞKA bir yerde görünür kalmalı. `hasLiveMismatch`
// BİLEREK `validityState`'e BAKMAZ (§21.1 madde 1, aşağıda DEĞİŞMEDİ).
//
// **S32g (`docs/reviews/S32g-eligibility-checklist.md` §4.4, api-contract §29.5 S32g bloğu) —
// kapı uygunluk kontrol listesi, `VerdictCard.tsx` ile PAYLAŞILAN `EligibilityChecklist.tsx`
// (`lib/eligibility.ts` tek doğruluk kaynağı, kartla AYNI G1 riskine karşı).** `asOf`'un
// ALTINDA, `İZİNLER` listesinin ÜSTÜNDE — bir üç-saat-önceki `Denied` kaydın yanında "tamam"
// görünen bir kontrol listesi o reddin YANLIŞ olduğunu GÖSTERMEZ (§29.5 kayıt bütünlüğü maddesi),
// bu yüzden sıra SABİTTİR. Modal bir KAYITTIR: yönlendirme cümlesi (kartın `security.eligibility.
// route.*`'i) YOK, ÜÇ satır da HER ZAMAN (`Allowed` VE `Denied`) render edilir, öğeler
// KIRPILMADAN (`break-words`) satırın altında — ikinci bir kaydırma kutusu AÇILMAZ (`Modal`
// zaten kaydırır, S32d §7.3).

import type {
  ApiError,
  GateScanPermitDto,
  SecurityEntryDetailDto,
  SecurityEntryRowDto,
} from '@isgportal/shared';
import {
  Alert,
  Button,
  Modal,
  Spinner,
  Tag,
  cn,
  currentCulture,
  securityApi,
  useDocumentTypes,
} from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { CircleCheck, HardHat, OctagonX, TriangleAlert } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { eligibilityRows, findEvaluatedPermit } from '../../lib/eligibility';
import { formatDateRange, formatDateTime, formatTime } from '../../lib/format';
import {
  PERMIT_CHIP_TAG_COLOR,
  PERMIT_CHIP_TEXT_CLASS,
  permitChip,
  permitWindow,
  rankPermits,
} from '../../lib/permitWindow';
import { securityKeys } from '../../lib/securityKeys';
import { reasonLabel } from '../../lib/securityReason';
import { VerdictAvatar } from './Avatar';
import { EligibilityChecklist } from './EligibilityChecklist';
import { MethodMarker } from './MethodMarker';

export interface EntryDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Seçili SATIR OBJESİ — bir dizi indeksi DEĞİL (tasarım spec'i §7.3, F9 defekti). */
  row: SecurityEntryRowDto | null;
}

export function EntryDetailModal({ open, onOpenChange, row }: EntryDetailModalProps) {
  const { t } = useTranslation();
  const { label } = useDocumentTypes();
  const culture = currentCulture();
  const uuid = row?.uuid ?? null;
  const permitsEyebrowId = useId();

  const detailQuery = useQuery<SecurityEntryDetailDto, ApiError>({
    queryKey: securityKeys.entryDetail(uuid ?? ''),
    // `enabled: open && uuid != null` garanti eder — assert yalnız BUNUN altında çalışır.
    queryFn: () => securityApi.getEntryDetail(uuid as string),
    enabled: open && uuid != null,
    staleTime: 0,
    gcTime: 0,
  });

  if (!row) {
    return null;
  }

  const isAllowed = row.result === 'Allowed';
  const deniedReasonLabel = !isAllowed ? reasonLabel(row.reason, t) : null;
  const permits: GateScanPermitDto[] = detailQuery.data?.permits ?? [];
  // §17 — `VerdictCard.tsx`'in AYNI `rankPermits`'i (tek doğruluk kaynağı, §17 madde 4): rank 0
  // aktif+hibe, 1 aktif hibesiz, 2 ileri tarihli, 3 süresi geçmiş.
  const rankedPermits = rankPermits(permits);
  // §21.1 madde 1 — BİLEREK `validityState`'e BAKMAZ: not "İzin bu okutmadan sonra DEĞİŞTİ" der,
  // kendiliğinden sona eren bir izin DEĞİŞMEMİŞTİR (S32e §1.2, S32f'de DEĞİŞMEDİ).
  const hasLiveMismatch =
    isAllowed && detailQuery.isSuccess && !permits.some((p) => p.entryPermitted);
  // S32g — kontrol listesi `VerdictCard.tsx` ile AYNI kaynaktan (`lib/eligibility.ts`), G1'in AYNI
  // riskine karşı. `permits` boşsa/`closestToEntry` hiçbir kalemde işaretli DEĞİLSE (backend S32g'yi
  // henüz DAĞITMADIYSA HER ZAMAN bu durum) `null`/`[]` — bölüm HİÇ render EDİLMEZ.
  const evaluatedPermit = findEvaluatedPermit(permits);
  const eligibility = evaluatedPermit ? eligibilityRows(evaluatedPermit, label) : [];

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t('security.detail.title')}
      description={row.personnelName}
      closeLabel={t('security.common.close')}
      size="lg"
      footer={
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          {t('security.common.close')}
        </Button>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Tag color={isAllowed ? 'green' : 'red'}>
              {isAllowed ? (
                <CircleCheck className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              ) : (
                <OctagonX className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              )}
              {isAllowed ? t('security.day.status.allowed') : t('security.day.status.denied')}
            </Tag>
            <span className="text-sm tabular-nums text-muted">
              {formatDateTime(row.at, culture)}
            </span>
          </div>

          <div className="flex min-w-0 gap-3">
            <VerdictAvatar name={row.personnelName} />
            <div className="flex min-w-0 flex-col justify-center gap-1">
              <p className="break-words text-lg font-semibold leading-tight text-fg">
                {row.personnelName}
              </p>
              <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted">
                {row.contractorName ? (
                  <span className="inline-flex min-w-0 items-center gap-1">
                    <HardHat className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    <span className="sr-only">{t('security.contractor.srLabel')}</span>
                    <span className="min-w-0 break-words" title={row.contractorName}>
                      {row.contractorName}
                    </span>
                  </span>
                ) : null}
                {row.contractorName ? <span aria-hidden="true">·</span> : null}
                <MethodMarker method={row.method} selfAssertedSuffix={row.method === 'GateQr'} />
              </div>
            </div>
          </div>
        </div>

        {!isAllowed ? (
          <section className="flex flex-col gap-1.5 rounded-md bg-danger-subtle p-3">
            <h3 className="text-xs font-semibold text-muted">
              {t('security.detail.section.reason')}
            </h3>
            <p className="text-sm font-bold text-danger">{deniedReasonLabel}</p>
            <p className="text-sm text-fg">
              {t('security.detail.deniedAt', { time: formatTime(row.at, culture) })}
            </p>
          </section>
        ) : null}

        <section className="flex flex-col gap-2 border-t border-border pt-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-xs font-semibold text-muted">
              {t('security.detail.permits.title')}
            </h3>
            {hasLiveMismatch ? (
              <Tag color="gray" className="shrink-0 gap-1">
                <TriangleAlert className="h-3 w-3 shrink-0" aria-hidden="true" />
                {t('security.verdict.liveMismatch')}
              </Tag>
            ) : null}
          </div>

          {detailQuery.isPending ? (
            <div className="flex items-center justify-center py-6">
              <Spinner label={t('security.common.loading')} />
            </div>
          ) : detailQuery.isError ? (
            <div className="flex flex-col items-center gap-2 py-4 text-center">
              <Alert tone="danger">{t('security.detail.error')}</Alert>
              <button
                type="button"
                onClick={() => detailQuery.refetch()}
                className="rounded text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {t('security.gate.retry')}
              </button>
            </div>
          ) : detailQuery.isSuccess ? (
            permits.length === 0 ? (
              <p className="text-sm text-muted">{t('security.detail.permits.empty')}</p>
            ) : (
              <>
                <p className="text-xs text-muted">
                  {t('security.detail.permits.asOf', {
                    time: formatDateTime(detailQuery.data.liveAsOf, culture),
                  })}
                </p>
                {/* S32g, tasarım otoritesi §4.4 — `asOf`'un ALTINDA, `İZİNLER` listesinin
                    ÜSTÜNDE. Modal bir KAYITTIR: yönlendirme cümlesi YOK, ÜÇ satır da HER ZAMAN
                    (`Allowed` VE `Denied`) render edilir, öğeler KIRPILMADAN satırın altında. */}
                {evaluatedPermit && eligibility.length > 0 ? (
                  <>
                    <EligibilityChecklist
                      permit={evaluatedPermit}
                      rows={eligibility}
                      density="record"
                    />
                    <span id={permitsEyebrowId} className="text-[11px] font-semibold text-muted">
                      {t('security.verdict.permits.eyebrow', { count: rankedPermits.length })}
                    </span>
                  </>
                ) : null}
                <ul
                  aria-labelledby={eligibility.length > 0 ? permitsEyebrowId : undefined}
                  className="flex flex-col divide-y divide-border"
                >
                  {rankedPermits.map((permit, index) => {
                    const chip = permitChip(permit);
                    const ChipIcon = chip.icon;
                    const windowState = permitWindow(permit);
                    // §16 [taste, modal only] — pencere dışı bir satırda rozet artık PENCEREYİ
                    // söylüyor ("ileri tarihli"/"süresi geçmiş"), hibe olgusu bu yüzden alt
                    // satıra GEÇMİŞ-ZAMAN ekiyle taşınır ("var" bir ŞİMDİ iddiası, "verilmiş" bir
                    // OLGU — §16 tense notu).
                    const grantFragment =
                      windowState !== 'active'
                        ? t(
                            permit.entryPermitted
                              ? 'security.verdict.permit.grantedPast'
                              : 'security.verdict.permit.notGrantedPast',
                          )
                        : null;
                    return (
                      <li
                        key={`${permit.title}-${permit.validityStart}-${index}`}
                        className="flex items-start gap-2 py-2"
                      >
                        <ChipIcon
                          className={cn(
                            'mt-0.5 h-4 w-4 shrink-0',
                            PERMIT_CHIP_TEXT_CLASS[chip.tone],
                          )}
                          aria-hidden="true"
                        />
                        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="break-words text-sm font-medium text-fg">
                              {permit.title}
                            </span>
                            <Tag color={PERMIT_CHIP_TAG_COLOR[chip.tone]} className="shrink-0">
                              {t(chip.labelKey)}
                            </Tag>
                          </div>
                          <span className="text-xs text-muted">
                            {formatDateRange(permit.validityStart, permit.validityEnd, culture)}
                            {permit.siteName ? ` · ${permit.siteName}` : ''}
                            {grantFragment ? ` · ${grantFragment}` : ''}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </>
            )
          ) : null}
        </section>
      </div>
    </Modal>
  );
}
